// ui/web/src/components/buzz/BuzzSidebarPanel.tsx
import { useState, useEffect } from "react"
import { useBuzzSession } from "@/shared/hooks/useBuzzSession"
import { BuzzConnectionBadge } from "./BuzzConnectionBadge"
import { BuzzEmbeddedClient } from "./BuzzEmbeddedClient"
import { BuzzLoadingState } from "./BuzzLoadingState"
import { BuzzErrorState } from "./BuzzErrorState"
import { ResizeDragHandle } from "./ResizeDragHandle"
import { SvgX as XIcon, SvgBubbleText as MessageCircleIcon } from "@opal/icons"
import { cn } from "@/shared/lib/utils"

interface BuzzSidebarPanelProps {
  defaultOpen?: boolean
  defaultWidth?: number   // px, default 380
  onUnreadCountChange?: (count: number) => void
}

export function BuzzSidebarPanel({
  defaultOpen = false,
  defaultWidth = 380,
  onUnreadCountChange,
}: BuzzSidebarPanelProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [width, setWidth] = useState(defaultWidth)
  const { status, sessionToken, relayUrl, refetch } = useBuzzSession()

  // Sync state if defaultOpen changes
  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  // Keyboard shortcut: Cmd/Ctrl+Shift+B
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "B") {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  return (
    <>
      {/* Sliding panel */}
      <div
        id="buzz-sidebar-panel"
        className={cn(
          "fixed right-0 top-0 h-full bg-[var(--surface-01)] shadow-xl z-40 flex flex-col",
          "transition-transform duration-300 ease-in-out",
          open ? "translate-x-0" : "translate-x-full"
        )}
        style={{ width }}
        role="complementary"
        aria-label="Buzz chat panel"
      >
        {/* Drag-to-resize handle */}
        <ResizeDragHandle onResize={setWidth} minWidth={280} maxWidth={640} />

        {/* Panel header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-01)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircleIcon className="size-4 text-[var(--color-primary)]" aria-hidden />
            <span className="text-sm font-medium text-[var(--text-01)]">Buzz</span>
          </div>
          <div className="flex items-center gap-2">
            <BuzzConnectionBadge status={status} />
            <button
              id="btn-close-buzz-panel"
              onClick={() => setOpen(false)}
              className="btn btn-ghost btn-icon"
              aria-label="Close Buzz panel"
            >
              <XIcon className="size-4" aria-hidden />
            </button>
          </div>
        </div>

        {/* Panel body */}
        <div className="flex-1 relative overflow-hidden min-h-0">
          {status === "loading" && <BuzzLoadingState />}
          {status === "error"   && <BuzzErrorState onRetry={refetch} />}
          {status === "connected" && sessionToken && relayUrl && (
            <BuzzEmbeddedClient
              relayUrl={relayUrl}
              sessionToken={sessionToken}
              mode="sidebar"
              panelWidth={width}
              onUnreadCountChange={onUnreadCountChange}
            />
          )}
        </div>
      </div>

      {/* Click-outside overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  )
}
